/**
 * Per-site panel feature toggles («تنظیمات اولیه وبسایت»). Keys: 'g:<group id>' for a sidebar group,
 * the route path for an item. Stored in the site's DB (site_settings.panel_modules_disabled) via
 * /api/admin/features/panel_modules*, cached per site in localStorage for the router guard.
 * Default: everything on. The dashboard and the settings group/page can never be switched off.
 */
import nav from '@/containers/_nav'
import axios from '@/api/http'
import { getSiteId } from '@/utils/site'

/** First matching marker route in a sidebar group maps to this stable group id. */
const GROUP_MARKERS = {
  shop: ['/dashboard/orders/list', '/dashboard/products/list', '/dashboard/settings/shop'],
  content: ['/dashboard/news/all-news', '/dashboard/pages/list', '/dashboard/media'],
  comments: ['/dashboard/news/comments'],
  forms: ['/dashboard/forms/list'],
  reports: ['/dashboard/reports/report-date', '/dashboard/reports/analytics'],
  display: ['/dashboard/display/sliders'],
  users: ['/dashboard/users/admins', '/dashboard/orders/customers'],
  tools: ['/dashboard/settings/redirect'],
  settings: ['/dashboard/settings/main-setting'],
}

/** Legacy group ids from the pre-restructure sidebar (still honored in disabled lists). */
const LEGACY_ROUTE_GROUP = {
  '/dashboard/news/all-news': 'g:blog',
  '/dashboard/news/add-news': 'g:blog',
  '/dashboard/news/categories/0': 'g:blog',
  '/dashboard/news/tags': 'g:blog',
  '/dashboard/media': 'g:media',
  '/dashboard/pages/list': 'g:pages',
  '/dashboard/pages/add': 'g:pages',
  '/dashboard/products/list': 'g:products',
  '/dashboard/products/create': 'g:products',
  '/dashboard/products/category/0': 'g:products',
  '/dashboard/products/tags': 'g:products',
  '/dashboard/products/property-templates': 'g:products',
  '/dashboard/products/additional-options': 'g:products',
  '/dashboard/products/product-bundles': 'g:products',
  '/dashboard/products/brands': 'g:products',
  '/dashboard/products/digikala-bulk': 'g:products',
  '/dashboard/forms/list': 'g:tools',
}

export const PROTECTED = new Set(['g:settings', '/dashboard', '/dashboard/settings/main-setting'])

const routeOf = (it) => (typeof it.to === 'string' ? it.to : (it.to && it.to.path) || '')

export function groupId(g) {
  const routes = (g.items || []).map(routeOf)
  for (const [id, markers] of Object.entries(GROUP_MARKERS)) {
    if (markers.some((r) => routes.includes(r))) return id
  }
  return String(g.name || '').replace(/[^a-z0-9_-]/gi, '').toLowerCase() || 'group'
}

/** The deduplicated sidebar as a tree: [{ key, name, icon, protected, items:[{ key, name, protected }] }] */
export function moduleTree() {
  const out = []
  ;((nav[0] && nav[0]._children) || []).forEach((g) => {
    if (g.items && g.items.length) {
      const key = 'g:' + groupId(g)
      out.push({
        key, name: g.name, protected: PROTECTED.has(key),
        items: g.items.map((it) => ({ key: routeOf(it), name: it.name, protected: PROTECTED.has(routeOf(it)) || PROTECTED.has(key) })).filter((x) => x.key),
      })
    } else if (routeOf(g)) {
      out.push({ key: routeOf(g), name: g.name, protected: PROTECTED.has(routeOf(g)), items: [] })
    }
  })
  return out
}

const storeKey = () => 'panel_modules:' + getSiteId()

export function getDisabled() {
  try {
    const arr = JSON.parse(localStorage.getItem(storeKey()) || '[]')
    return new Set((Array.isArray(arr) ? arr : []).filter((k) => !PROTECTED.has(k)))
  } catch (e) { return new Set() }
}

export function setDisabled(list) {
  const clean = (Array.isArray(list) ? list : []).filter((k) => typeof k === 'string' && !PROTECTED.has(k))
  try { localStorage.setItem(storeKey(), JSON.stringify(clean)) } catch (e) { /* ignore */ }
  window.dispatchEvent(new Event('panel-modules'))
}

function legacyRouteOff(key, disabled) {
  const leg = LEGACY_ROUTE_GROUP[key]
  return !!(leg && disabled.has(leg))
}

/** off = disabled itself, or its parent group is disabled */
export function isKeyOff(key, disabled = getDisabled()) {
  if (!key || PROTECTED.has(key)) return false
  if (disabled.has(key)) return true
  if (key.indexOf('g:') === 0) return false
  if (legacyRouteOff(key, disabled)) return true
  const g = moduleTree().find((x) => x.items.some((it) => it.key === key))
  return !!(g && disabled.has(g.key))
}

/** every listed feature key is on */
export function featuresOn(keys, disabled = getDisabled()) {
  return (keys || []).every((k) => !isKeyOff(k, disabled))
}

function matches(path, key) {
  const p = String(path || '').replace(/\/+$/, '')
  const k = String(key || '').replace(/\/+$/, '')
  if (!k || k === '/dashboard') return false
  if (p === k || p.indexOf(k + '/') === 0) return true
  // list routes with a trailing id param (/categories/0, /menus/0) cover their siblings
  const base = k.replace(/\/0$/, '')
  return base !== k && (p === base || p.indexOf(base + '/') === 0)
}

/** a direct URL to a switched-off feature */
export function isRouteOff(path, disabled = getDisabled()) {
  if (!disabled.size) return false
  const p = String(path || '')
  if (p.replace(/\/+$/, '') === '/dashboard' || matches(p, '/dashboard/settings/main-setting')) return false
  return moduleTree().some((g) => {
    const gOff = disabled.has(g.key) && !g.protected
    if (!g.items.length) return disabled.has(g.key) && matches(p, g.key)
    return g.items.some((it) => !it.protected && (gOff || disabled.has(it.key) || legacyRouteOff(it.key, disabled)) && matches(p, it.key))
  })
}

export function loadModules() {
  return axios.post('/api/admin/features/panel_modules', {}, { show_pros: false }).then((r) => {
    if (r.data && Array.isArray(r.data.disabled)) setDisabled(r.data.disabled)
    return getDisabled()
  }).catch(() => getDisabled())
}

export function saveModules(list) {
  return axios.post('/api/admin/features/panel_modules_save', { disabled: list }, {}).then((r) => {
    if (r.data && !r.data.error) setDisabled(r.data.disabled || list)
    return r
  })
}

export const MODULE_OFF_NOTICE = '\u0627\u06cc\u0646 \u0628\u062e\u0634 \u062f\u0631 \u062a\u0646\u0638\u06cc\u0645\u0627\u062a \u0627\u0648\u0644\u06cc\u0647 \u0648\u0628\u0633\u0627\u06cc\u062a \u063a\u06cc\u0631\u0641\u0639\u0627\u0644 \u0627\u0633\u062a.'

/** small transient notice (top of the page), used by the router guard */
export function showNotice(msg) {
  try {
    const el = document.createElement('div')
    el.className = 'panel-notice'
    el.setAttribute('role', 'status')
    el.textContent = msg
    el.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:3000;background:#1f2937;color:#fff;padding:10px 18px;border-radius:10px;font-size:13px;box-shadow:0 8px 24px rgba(0,0,0,.18);direction:rtl;max-width:90vw'
    document.body.appendChild(el)
    setTimeout(() => { el.remove() }, 4000)
  } catch (e) { /* ignore */ }
}
